import React, {
  useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ExpandButton from '../../../components/expand-button/expand-button.component';
import ActionButton from '../../../components/action-button/action-button.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Loader from '../../../components/loader/loader.component';
import TextHeader from '../../../components/text-header/text-header.component';
import Hint from '../../../components/hint/hint.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldPhone from '../../../components/form-field-phone/form-field-phone.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldAddress from '../../../components/form-field-address/form-field-address.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import FieldAddressBlock from '../../../components/field-address-block/field-address-block.component';
import FieldPhoneBlock from '../../../components/field-phone-block/field-phone-block.component';
import ImageFileContainer from '../../../components/image-file-container/image-file-container.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../icons/remove-icon';
import FormService from '../../../services/form-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../constants/entity/contact-types';
import URL_LINKEDIN_PROFILE_PREFIX from '../../../constants/system/urls';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const ContactModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isContactPageForm,
    currentUser,
    contactPhotoFile,
    contactPhotoPreviewUrl,
    contactAddressNewItems,
    contactAddressDetailsItems,
    contactGroupList,
    contactTypeList,
    contactCompanyList,
    assetTypeList,
    tagList,
    saveFormDeps,
    onCreate,
    onCancel,
    onSubmitCreateForm,
    onContactPhotoChange,
    onContactPhotoDelete,
    onContactGroupsChange,
    onTagsChange,
    onCloseModal,
    onAssetTypesChange,
    onPhoneChange,
    onPhoneWorkChange,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onWebsiteChange,
    onEmailChange,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
    onContactAddressDetailsClose,
    onContactAddressDetailsOpen,
    onContactAddressDeleteItemClick,
    onContactAddressAddNewItem,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [selectedTags, setSelectedTags]: [Tag[], Function] = useState([]);
  const [contactType, setContactType]: [string, Function]
    = useState(isContactPageForm ? CONTACT_TYPE_PERSON : CONTACT_TYPE_COMPANY);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType[], Function] = useState([]);
  const [selectedContactGroups, setSelectedContactGroups]: [ContactGroup[], Function] = useState([]);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);

  const contactId: number = FormService.FORM_KEY_NEW;
  const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
  const formPrefix: string = isContactPageForm ? 'contactData.newContact' : `contactListData[${formKeyContact}]`;
  const withIsDefaultColumn: boolean = currentUser && currentUser.isPlatformAdmin;
  const isPersonContact: boolean = contactType === CONTACT_TYPE_PERSON;

  const menuItemContactTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactTypeList,
  }), [contactTypeList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: contactCompanyList,
    optionItemImageKey: 'photo',
    optionItemImageThumbnailKey: 'photoThumbnail',
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemContactGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactGroupList,
    selectedItemList: selectedContactGroups,
  }), [contactGroupList, selectedContactGroups]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(contactId, assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, [contactId]);

  const phoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (onPhoneChange) {
      onPhoneChange(contactId, phoneValue);
    }
  }, [contactId]);

  const phoneWorkChange: PhoneChangeHandlerFunction = useCallback((phoneWorkValue?: string): void => {
    if (onPhoneWorkChange) {
      onPhoneWorkChange(contactId, phoneWorkValue);
    }
  }, [contactId]);

  const contactGroupsChange: Function = useCallback((contactGroupValues: ContactGroup[]): void => {
    if (onContactGroupsChange) {
      onContactGroupsChange(contactId, contactGroupValues);
    }

    setSelectedContactGroups(contactGroupValues);
  }, [contactId]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(contactId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [contactId]);

  const contactPhotoChange: Function = useCallback((file: File): void => {
    if (onContactPhotoChange) {
      onContactPhotoChange(contactId, file);
    }
  }, [contactId]);

  const contactPhotoDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onContactPhotoDelete) {
      onContactPhotoDelete(contactId);
    }

    event.stopPropagation();
  }, [contactId]);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    onSubmitCreateForm((): void => {
      if (onCreate) {
        onCreate((): void => {
          setSelectedAssetTypes([]);
          setSelectedTags([]);
          setSelectedContactGroups([]);
          setContactType(isContactPageForm ? CONTACT_TYPE_PERSON : CONTACT_TYPE_COMPANY);
        });
      }
    });
  }, [isSaveDisabled, isContactPageForm, contactPhotoFile, ...(saveFormDeps || [])]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel) {
      onCancel();
    }

    setSelectedAssetTypes([]);
    setSelectedTags([]);
    setSelectedContactGroups([]);
    setContactType(isContactPageForm ? CONTACT_TYPE_PERSON : CONTACT_TYPE_COMPANY);

    if (onCloseModal) {
      onCloseModal();
    }
  }, [isContactPageForm]);

  const contactTypeChange: Function = useCallback((contactTypeValue: ContactType): void => {
    setContactType(contactTypeValue);
  }, []);

  const websiteChange: Function = useCallback((websiteValue: string): void => {
    if (onWebsiteChange && contactType === CONTACT_TYPE_COMPANY) {
      onWebsiteChange(contactId, websiteValue);
    }
  }, [contactId, contactType]);

  const emailChange: Function = useCallback((emailValue: string): void => {
    if (onEmailChange && contactType === CONTACT_TYPE_PERSON) {
      onEmailChange(contactId, emailValue);
    }
  }, [contactId, contactType]);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled) {
      setIsLeaveConfirmModalOpen(true);
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  return (
    <ModalLayout
      isOpen={isOpen}
      title="New Contact"
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
    >
      <Form>
        <Card isContainer withNoPadding>
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              <TableRow>
                <TableCell colSpan={COLUMNS_TOTAL}>
                  <TextHeader isSubHeader>
                    General
                  </TextHeader>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Type
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    isDisabled={isContactPageForm || (contactTypeList && !contactTypeList.length)}
                    name={`${formPrefix}.type`}
                    optionsTotal={contactTypeList && contactTypeList.length}
                    onChange={((contactTypeName: string): void => {
                      contactTypeChange(contactTypeName);
                    })}
                  >
                    {menuItemContactTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Groups
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isDisabled={contactGroupList && !contactGroupList.length}
                    keyField="name"
                    name={`${formPrefix}.groupIds`}
                    optionList={contactGroupList}
                    onValuesChange={contactGroupsChange}
                  >
                    {menuItemContactGroupList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
              {
                contactType === CONTACT_TYPE_COMPANY && (
                  <TableRow>
                    <TableCell>
                      Name
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.name`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                contactType === CONTACT_TYPE_PERSON && (
                  <TableRow>
                    <TableCell>
                      First Name
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.firstName`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                contactType === CONTACT_TYPE_PERSON && (
                  <TableRow>
                    <TableCell>
                      Last Name
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.lastName`} />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell colSpan={COLUMNS_TOTAL}>
                  <TextHeader isSubHeader>
                    Contact Information
                  </TextHeader>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Phone
                </TableCell>
                <TableCell>
                  <FieldPhoneBlock>
                    <FormFieldPhone
                      isBordered
                      name={`${formPrefix}.phone`}
                      onChange={phoneChange}
                    />
                    <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.phoneExtension`} placeholder="Ext" />
                  </FieldPhoneBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Work Phone
                </TableCell>
                <TableCell>
                  <FieldPhoneBlock>
                    <FormFieldPhone
                      isBordered
                      name={`${formPrefix}.phoneWork`}
                      onChange={phoneWorkChange}
                    />
                    <FormFieldNumber
                      isBordered
                      withNoCommas
                      name={`${formPrefix}.phoneWorkExtension`}
                      placeholder="Ext"
                    />
                  </FieldPhoneBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Email
                  {
                    contactType === CONTACT_TYPE_PERSON && (
                      <Hint
                        title={'Fill in Email field to search and automatically populate other fields '
                          + 'of the person, if it is listed in our dictionary'}
                      />
                    )
                  }
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    name={`${formPrefix}.email`}
                    onChange={(emailValue: string): void => {
                      emailChange(emailValue);
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  LinkedIn
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    name={`${formPrefix}.linkedin`}
                    placeholder={`${URL_LINKEDIN_PROFILE_PREFIX}...`}
                  />
                </TableCell>
              </TableRow>
              {
                contactType === CONTACT_TYPE_COMPANY && (
                  <TableRow>
                    <TableCell>
                      Website
                      <Hint
                        title={'Fill in Website field to search and automatically populate other fields '
                          + 'of the company, if it is listed in our dictionary'}
                      />
                    </TableCell>
                    <TableCell>
                      <FormFieldText
                        isBordered
                        name={`${formPrefix}.website`}
                        onChange={(websiteValue: string): void => {
                          websiteChange(websiteValue);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell>
                  Main Address
                </TableCell>
                <TableCell>
                  <FieldAddressBlock>
                    <FormFieldAddress
                      isModal
                      isBordered
                      itemId={contactId}
                      name={`${formPrefix}.streetAddress`}
                      onAddressSearchCompleted={(addressObject: JsonObject): void => {
                        onAddressSearchCompleted(addressObject, contactId);
                      }}
                      onKeyDown={(): void => {
                        onAddressFieldKeyDown(contactId);
                      }}
                    />
                    <FormFieldText isDisabled isBordered name={`${formPrefix}.location`} />
                    <FormFieldText isDisabled isBordered name={`${formPrefix}.postalCode`} />
                    <FormFieldText isHidden name={`${formPrefix}.address`} />
                  </FieldAddressBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Main Address Details
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    isMultiline
                    name={`${formPrefix}.addressDetails`}
                    maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
                    minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                  />
                </TableCell>
              </TableRow>
              {
                ((contactAddressNewItems && Object.values(contactAddressNewItems)) || []).map((
                  contactAddressItem: ContactAddress,
                ): ReactElement => {
                  const formKeyContactAddress: string = FormService.getIndexedFormFieldKey(
                    contactAddressItem.id,
                  );
                  const isAddressDetailsVisible: boolean = contactAddressDetailsItems
                  && contactAddressDetailsItems.length
                    ? contactAddressDetailsItems.includes(contactAddressItem.id) : false;

                  return (
                    <Fragment key={contactAddressItem.id}>
                      <TableRow>
                        <TableCell>
                          <FlexContainer>
                            <FlexContainer>
                              <ExpandButton
                                isOpen={isAddressDetailsVisible}
                                onClick={(): void => {
                                  if (isAddressDetailsVisible) {
                                    onContactAddressDetailsClose(contactId, contactAddressItem.id);
                                  } else {
                                    onContactAddressDetailsOpen(contactId, contactAddressItem.id);
                                  }
                                }}
                              />
                            </FlexContainer>
                            <FormFieldText isBordered name={`${formPrefix}.addresses[${formKeyContactAddress}].name`} />
                          </FlexContainer>
                        </TableCell>
                        <TableCell>
                          <FieldAddressBlock>
                            <FormFieldAddress
                              isBordered
                              itemId={`${contactId}-${contactAddressItem.id}`}
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].streetAddress`}
                              onAddressSearchCompleted={(addressObject: JsonObject): void => {
                                onAddressSearchCompleted(addressObject, contactId, contactAddressItem.id);
                              }}
                              onKeyDown={(): void => {
                                onAddressFieldKeyDown(contactId, contactAddressItem.id);
                              }}
                            />
                            <FormFieldText
                              isDisabled
                              isBordered
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].location`}
                            />
                            <FormFieldText
                              isDisabled
                              isBordered
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].postalCode`}
                            />
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}.addresses[${formKeyContactAddress}].address`}
                            />
                            <Tooltip title="Delete">
                              <ActionButton
                                isInline
                                withOffset
                                withSmallPadding
                                onClick={(): void => {
                                  onContactAddressDeleteItemClick(contactId, contactAddressItem.id);
                                }}
                              >
                                <RemoveIcon />
                              </ActionButton>
                            </Tooltip>
                          </FieldAddressBlock>
                        </TableCell>
                      </TableRow>
                      {
                        isAddressDetailsVisible && (
                          <TableRow>
                            <TableCell>
                              Address Details
                            </TableCell>
                            <TableCell>
                              <FormFieldText
                                isBordered
                                isMultiline
                                name={`${formPrefix}.addresses[${formKeyContactAddress}].addressDetails`}
                                maxRows={FormService.TEXTAREA_MAX_ROWS_DEFAULT}
                                minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      }
                    </Fragment>
                  );
                })
              }
              <TableRowAddNewItem
                colSpan={COLUMNS_TOTAL}
                label="Add New Address"
                onClick={(): void => {
                  onContactAddressAddNewItem(contactId);
                }}
              />
              <TableRow>
                <TableCell colSpan={COLUMNS_TOTAL}>
                  <TextHeader isSubHeader>
                    Details
                  </TextHeader>
                </TableCell>
              </TableRow>
              {
                withIsDefaultColumn && (
                  <TableRow>
                    <TableCell>
                      Is Default
                    </TableCell>
                    <TableCell>
                      <FormFieldCheckbox name={`${formPrefix}.isDefault`} />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell>
                  Photo
                </TableCell>
                <TableCell>
                  <ImageFileContainer withSubBlock>
                    <FormFieldUpload
                      isImage
                      isDroppable
                      isSmallImage
                      name={`${formPrefix}.photo`}
                      uploadFile={contactPhotoFile}
                      defaultPreviewUrl={contactPhotoPreviewUrl
                        || (isPersonContact ? PersonDefaultImage : CompanyDefaultImage)}
                      buttonLabel="New Contact"
                      acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                      onFileChange={contactPhotoChange}
                      onFileDelete={contactPhotoDelete}
                    />
                    <FormFieldText isHidden name={`${formPrefix}.photoLink`} />
                  </ImageFileContainer>
                </TableCell>
              </TableRow>
              {
                contactType === CONTACT_TYPE_PERSON && (
                  <TableRow>
                    <TableCell>
                      Title
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.title`} />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell>
                  Description
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.description`} />
                </TableCell>
              </TableRow>
              {
                contactType === CONTACT_TYPE_PERSON && (
                  <TableRow>
                    <TableCell>
                      Company
                    </TableCell>
                    <TableCell>
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={isContactPageForm || (contactCompanyList && !contactCompanyList.length)}
                        name={`${formPrefix}.companyId`}
                        optionsTotal={contactCompanyList && contactCompanyList.length}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                      >
                        {menuItemContactCompanyList}
                      </FormFieldSelect>
                    </TableCell>
                  </TableRow>
                )
              }
              {
                contactType === CONTACT_TYPE_COMPANY && (
                  <TableRow>
                    <TableCell>
                      Industry
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.industry`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                contactType === CONTACT_TYPE_COMPANY && (
                  <TableRow>
                    <TableCell>
                      Foundation Year
                    </TableCell>
                    <TableCell>
                      <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.foundationYear`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                contactType === CONTACT_TYPE_COMPANY && (
                  <TableRow>
                    <TableCell>
                      Headquarter
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.headQuarter`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                contactType === CONTACT_TYPE_COMPANY && (
                  <TableRow>
                    <TableCell>
                      Key Subsidiaries
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered name={`${formPrefix}.keySubsidiaries`} />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell>
                  Asset Focus
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isDisabled={assetTypeList && !assetTypeList.length}
                    keyField="name"
                    name={`${formPrefix}.assetTypes`}
                    optionList={assetTypeList}
                    onValuesChange={assetTypesChange}
                  >
                    {menuItemAssetTypeList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Tags
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isAbleToAdd
                    withSearch
                    isDisabled={tagList && !tagList.length}
                    keyField="name"
                    name={`${formPrefix}.tags`}
                    optionList={tagList}
                    onSearch={onTagSearch}
                    onClose={onTagSearchClose}
                    onValuesChange={tagsChange}
                  >
                    {menuItemTagList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
        <ModalButtonBlock isFixed>
          <Button onClick={cancelChangesAndCloseModal}>
            Cancel
          </Button>
          <Button isActive isDisabled={isSaving} onClick={applyChangesAndCloseModal}>
            Save
          </Button>
          {
            isSaving && !isSmallScreen && (
              <Loader isInline isModal isFloating isSmall />
            )
          }
        </ModalButtonBlock>
      </Form>
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default ContactModal;
